// SPDX-License-Identifier: Apache-2.0
import GirafeHTMLElement from '../../base/GirafeHTMLElement';
import IGirafePanel from '../../tools/state/igirafepanel';
import {
  AVAILABLE_CELL_COMPONENTS,
  CellComponent,
  CellContent,
  distributeCells,
  getDefaultSizes,
  getGridSize,
  isCellComponent,
  LayoutType
} from '../../tools/state/layout';

const LAYOUT_ICONS: Record<LayoutType, string> = {
  '1x1': 'icons/layout-1x1.svg',
  '2x1': 'icons/layout-2x1.svg',
  '1x2': 'icons/layout-1x2.svg',
  '2x2': 'icons/layout-2x2.svg'
};

class LayoutComponent extends GirafeHTMLElement implements IGirafePanel {
  templateUrl = './template.html';
  styleUrls = ['../../styles/common.css', './style.css'];

  isPanelVisible = false;
  panelTitle = 'layout-panel';
  panelTogglePath = 'interface.layoutPanelVisible';

  /** The Components the user can choose between for each Cell of the Grid. */
  availableComponents = AVAILABLE_CELL_COMPONENTS;

  public constructor() {
    super('layout');
    this.onShadowsToggle = this.onShadowsToggle.bind(this);
  }

  private get layout() {
    return this.state.layout;
  }

  /** The Layouts the user can choose between, as configured in the application. */
  get allowedLayouts(): LayoutType[] {
    return this.context.configManager.Config.interface.layouts;
  }

  /** The Layouts the user can choose between, with the icon illustrating each of them. */
  get layoutButtons(): { type: LayoutType; icon: string }[] {
    return this.allowedLayouts.map((type) => ({ type: type, icon: LAYOUT_ICONS[type] }));
  }

  /** The Cells of the current Layout, one line of the schema per row of the Grid. */
  get cellRows(): { index: number; content: CellContent }[][] {
    const { columns, rows } = getGridSize(this.layout.type as LayoutType);
    const cells = this.layout.cells as CellContent[];
    return Array.from({ length: rows }, (_, row) =>
      Array.from({ length: columns }, (_, column) => {
        const index = row * columns + column;
        return { index: index, content: cells[index] ?? null };
      })
    );
  }

  private registerEvents() {
    this.subscribe('layout.type', () => this.onLayoutChanged());
    this.subscribe('layout.cells', () => this.onLayoutChanged());
  }

  public togglePanel(visible: boolean) {
    this.isPanelVisible = visible;
    this.render();
  }

  private onLayoutChanged() {
    if (this.isPanelVisible) {
      this.refreshRender();
      this.syncSelects();
    }
  }

  /**
   * uHtml only updates the <selected> attribute of the options, which the browser ignores
   * as soon as the user has interacted with a select. The values are therefore applied explicitly,
   * so that the panel also follows the changes made outside of it (shared state, other components).
   */
  private syncSelects() {
    const cells = this.layout.cells as CellContent[];
    this.shadow.querySelectorAll<HTMLSelectElement>('.cell-select').forEach((select, index) => {
      select.value = cells[index] ?? '';
    });
  }

  onLayoutSelect(selectedLayout: LayoutType) {
    this.layout.cells = distributeCells(selectedLayout, this.layout.cells);
    this.layout.sizes = getDefaultSizes(selectedLayout);
    this.layout.type = selectedLayout;
  }

  /**
   * Places a Component in a Cell of the Grid.
   * A Component can only be displayed once: if it is already displayed in another Cell,
   * both Cells are swapped, so that the Component that was in the target Cell is not lost.
   */
  onCellSelect(event: Event, cellIndex: number) {
    const value = (event.target as HTMLSelectElement)?.value;
    const component: CellContent = isCellComponent(value) ? (value as CellComponent) : null;

    const cells = [...this.layout.cells] as CellContent[];
    const previousIndex = component === null ? -1 : cells.indexOf(component);
    if (previousIndex >= 0) {
      cells[previousIndex] = cells[cellIndex];
    }
    cells[cellIndex] = component;
    this.layout.cells = cells;
  }

  /** Whether one of the Cells displays a 3D Map, which the shadows options depend on. */
  get is3dDisplayed() {
    return (this.layout.cells as CellContent[]).includes('map-3d');
  }

  private onShadowsToggle(event: Event) {
    const shadowsDate = this.shadowRoot?.querySelector('.shadows-date') as HTMLElement;
    const isChecked = (event.target as HTMLInputElement).checked;

    shadowsDate?.classList.toggle('hidden', !isChecked);
    this.state.globe.shadows = isChecked;

    if (isChecked) {
      this.setDateTimePicker();
    }
  }

  private setDateTimePicker() {
    const timeDatePicker = this.shadowRoot?.querySelector('#shadowsDate') as HTMLInputElement;

    if (timeDatePicker) {
      const date = new Date();
      timeDatePicker.valueAsNumber = Math.round((date.valueOf() - date.getTimezoneOffset() * 60000) / 60000) * 60000;
      timeDatePicker.addEventListener('input', () => {
        this.update3dMapTimestamp(new Date(timeDatePicker.value));
      });
    }
  }

  private update3dMapTimestamp(date: Date) {
    this.state.globe.shadowsTimestamp = date.valueOf();
  }

  protected override connectedCallback() {
    super.connectedCallback();
    this.render();
    this.registerEvents();
  }

  override render() {
    if (this.isPanelVisible) {
      super.render();
      this.syncSelects();
    } else {
      this.hide();
    }
    super.girafeTranslate();
  }
}

export default LayoutComponent;
